// Org Console (StudioCore V2) — the org admin's school dashboard:
// print approval queue, user roster + print permissions, license pool, activity.
// All data flows through JWT-gated pse-org endpoints (orgApi); Realtime pings
// via OrgQueueContext only trigger refetches.

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useOrgQueue } from '../../contexts/OrgQueueContext';
import PrintQueueTab from './PrintQueueTab';
import UsersTab from './UsersTab';
import LicensesTab from './LicensesTab';
import ActivityTab from './ActivityTab';

// Brightivity kit look (white cards, pill buttons, Nunito) for the forked console's shared styles.
export const orgStyles = {
  card: { background: 'var(--bv-surface)', boxShadow: 'var(--bv-shadow-border)', borderRadius: '20px', padding: '16px' },
  text: { color: 'var(--bv-text)' },
  muted: { color: 'var(--bv-text-muted)' },
  btn: { padding: '8px 16px', borderRadius: '999px', border: 'none', background: 'var(--bv-fill)', color: 'var(--bv-text)', cursor: 'pointer', fontSize: '13px', fontWeight: 800, fontFamily: 'inherit' },
  btnPrimary: { padding: '8px 16px', borderRadius: '999px', border: 'none', background: 'var(--bv-green)', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 900, fontFamily: 'inherit', boxShadow: 'inset 0 -3px 0 var(--bv-green-edge)' },
  btnDanger: { padding: '8px 16px', borderRadius: '999px', border: 'none', background: 'var(--bv-coral-tint)', color: 'var(--bv-coral-on-tint)', cursor: 'pointer', fontSize: '13px', fontWeight: 800, fontFamily: 'inherit' },
};

export const STATUS_COLORS = {
  pending: '#7F4BF6', approved: '#1AB0F2', printed: '#26C774',
  rejected: '#FF5151', cancelled: '#545A78', expired: '#545A78',
};

const TABS = [
  { key: 'queue', labelKey: 'console.tabs.queue' },
  { key: 'users', labelKey: 'console.tabs.users' },
  { key: 'licenses', labelKey: 'console.tabs.licenses' },
  { key: 'activity', labelKey: 'console.tabs.activity' },
];

export default function OrgConsole({ hideHeader = false }) {
  const { t } = useTranslation('orgAdmin');
  const { user } = useAuth();
  const { pendingCount } = useOrgQueue();
  const [activeTab, setActiveTab] = useState('queue');

  if (!user?.orgAdmin) {
    return (
      <div style={{ padding: '48px', ...orgStyles.muted, fontFamily: 'inherit' }}>
        {t('console.accessRequired')}
      </div>
    );
  }

  return (
    <div style={{ padding: hideHeader ? 0 : '28px 32px', fontFamily: 'inherit', maxWidth: '1180px' }}>
      {/* Header (Brightivity: the page TopBar shows it) */}
      {!hideHeader && <div style={{ marginBottom: '20px' }}>
        <h1 style={{ ...orgStyles.text, fontSize: '22px', fontWeight: 700, margin: 0 }}>
          {user.schoolName || t('console.mySchool')}
        </h1>
        <div style={{ ...orgStyles.muted, fontSize: '13px', marginTop: '4px' }}>
          {t('console.subtitle')}
        </div>
      </div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid rgba(27,37,80,0.076)', paddingBottom: '0' }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 16px',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #7F4BF6' : '2px solid transparent',
              color: activeTab === tab.key ? '#7F4BF6' : 'rgba(27,37,80,0.57)',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
            }}
          >
            {t(tab.labelKey)}
            {tab.key === 'queue' && pendingCount > 0 && (
              <span style={{
                minWidth: '18px', height: '18px', padding: '0 5px', borderRadius: '9px',
                background: '#FF5151', color: '#fff', fontSize: '11px', fontWeight: 700,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {activeTab === 'queue' && <PrintQueueTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'licenses' && <LicensesTab />}
      {activeTab === 'activity' && <ActivityTab />}
    </div>
  );
}
